/**
 * CoOrdio Design System — icon adapter.
 * Re-exports Lucide icons as MUI-friendly components (accept className/sx and forward size).
 * Use these for nav, buttons, and UI; keeps icon set aligned with CoOrdio DS (lucide).
 */

import type { LucideProps } from 'lucide-react';
import {
  LayoutDashboard,
  SlidersHorizontal,
  BarChart3,
  Building2,
  Folder,
  Key,
  Bot,
  MessageSquare,
  Bug,
  Brain,
  Wrench,
  GitBranch,
  Calendar,
  Shield,
  CheckCircle,
  Cloud,
  Webhook,
  Settings,
  Link,
  Code,
  User,
  LogOut,
  AlertCircle,
  AlertTriangle,
  Info,
  Search,
  X,
  CircleX,
  FileWarning,
  Bell,
  Mail,
  MailOpen,
  Sun,
  Moon,
  Monitor,
  MoreVertical,
  Inbox,
  ClipboardList,
  Type,
  Hospital,
  ShieldCheck,
  Terminal,
} from 'lucide-react';

const defaultSize = 24;

/** Wraps Lucide icon so it works in MUI (ListItemIcon, etc.) — inherits font size via className/sx */
const wrap = (Icon: React.ComponentType<LucideProps>) => {
  const Wrapped = (props: LucideProps & { fontSize?: 'inherit' | 'small' | 'medium' | 'large' }) => {
    const { fontSize, ...rest } = props;
    const size = typeof props.size === 'number' ? props.size : defaultSize;
    return <Icon size={size} {...rest} />;
  };
  return Wrapped;
};

export const Icons = {
  LayoutDashboard: wrap(LayoutDashboard),
  SlidersHorizontal: wrap(SlidersHorizontal),
  BarChart3: wrap(BarChart3),
  Building2: wrap(Building2),
  Folder: wrap(Folder),
  Key: wrap(Key),
  Bot: wrap(Bot),
  MessageSquare: wrap(MessageSquare),
  Bug: wrap(Bug),
  Brain: wrap(Brain),
  Wrench: wrap(Wrench),
  GitBranch: wrap(GitBranch),
  Calendar: wrap(Calendar),
  Shield: wrap(Shield),
  CheckCircle: wrap(CheckCircle),
  Cloud: wrap(Cloud),
  Webhook: wrap(Webhook),
  Settings: wrap(Settings),
  Link: wrap(Link),
  Code: wrap(Code),
  User: wrap(User),
  LogOut: wrap(LogOut),
  AlertCircle: wrap(AlertCircle),
  AlertTriangle: wrap(AlertTriangle),
  Info: wrap(Info),
  Search: wrap(Search),
  X: wrap(X),
  XCircle: wrap(CircleX),
  FileWarning: wrap(FileWarning),
  Bell: wrap(Bell),
  Mail: wrap(Mail),
  MailOpen: wrap(MailOpen),
  Sun: wrap(Sun),
  Moon: wrap(Moon),
  Monitor: wrap(Monitor),
  MoreVertical: wrap(MoreVertical),
  Inbox: wrap(Inbox),
  ClipboardList: wrap(ClipboardList),
  Type: wrap(Type),
  Hospital: wrap(Hospital),
  ShieldCheck: wrap(ShieldCheck),
  Terminal: wrap(Terminal),
};

/** Raw Lucide icons for direct use when no wrapper needed */
export {
  LayoutDashboard,
  SlidersHorizontal,
  BarChart3,
  Building2,
  Folder,
  Key,
  Bot,
  MessageSquare,
  Bug,
  Brain,
  Wrench,
  GitBranch,
  Calendar,
  Shield,
  CheckCircle,
  Cloud,
  Webhook,
  Settings,
  Link,
  Code,
  User,
  LogOut,
  AlertCircle,
  AlertTriangle,
  Info,
  Search,
  X,
  CircleX as XCircle,
  FileWarning,
  Bell,
  Mail,
  MailOpen,
  Sun,
  Moon,
  Monitor,
  MoreVertical,
  Inbox,
  ClipboardList,
  Type,
  Hospital,
  ShieldCheck,
  Terminal,
};
